import { ErrorBox, Loader } from "@tfg/ui"
import { useTranslation } from "react-i18next"
import { Navigate, Outlet, useLocation } from "react-router-dom"
import type { LibraryResponses } from "@tfg/types/endpoints/library"

import { BlockedOnboardingRedirect } from "~/pages/SupplierPortal/Onboarding/components/BlockedOnboardingRedirect"
import { OnboardingGateRetryScreen } from "~/pages/SupplierPortal/Onboarding/components/OnboardingGateRetryBanner"
import {
  SUPPLIER_DEFAULT_HOME_PATH,
  SUPPLIER_ONBOARDING_ENTRY_PATH,
} from "~/pages/SupplierPortal/Onboarding/constants/onboardingGate.constants"
import { useOnboardingEntryDecision } from "~/pages/SupplierPortal/Onboarding/hooks/useOnboardingEntryDecision"
import { hasAdminOnboardingBypass } from "~/pages/SupplierPortal/Onboarding/utils/adminOnboardingBypass"
import { buildAuthenticatedNavigationTarget } from "~/pages/SupplierPortal/Onboarding/utils/buildAuthenticatedNavigationTarget"
import type { OnboardingEntryDecision } from "~/pages/SupplierPortal/Onboarding/utils/onboardingEntryDecision"
import {
  resolveGateState,
  type GateState,
} from "~/pages/SupplierPortal/Onboarding/utils/resolveGateState"
import { useAuth } from "~/store/auth-context"
import { isPlatformAdmin } from "~/utils/permissions"

export type { GateState }

type ReadyOnboardingGateProps = Readonly<{
  decision: OnboardingEntryDecision
  user: LibraryResponses.TFGUser
  getHomeRedirectUrl: (user: LibraryResponses.TFGUser) => string
  pathname: string
  invitedOrganizationId: string | null
}>

export const ReadyOnboardingGate = ({
  decision,
  user,
  getHomeRedirectUrl,
  pathname,
  invitedOrganizationId,
}: ReadyOnboardingGateProps) => {
  const isOnOnboardingRoute = pathname.startsWith(
    SUPPLIER_ONBOARDING_ENTRY_PATH
  )
  const hasAdminBypass =
    isPlatformAdmin(user) &&
    invitedOrganizationId !== null &&
    user.id.length > 0 &&
    hasAdminOnboardingBypass({
      supplierId: invitedOrganizationId,
      userId: user.id,
    })
  // Intentional: SUPER_ADMIN session skip may override BLOCKED for this tab.
  const outcome = hasAdminBypass ? "skip" : decision.outcome

  if (outcome === "block") {
    // Stay on the onboarding route for the not_related thank-you screen after
    // block; leaving the route still triggers logout redirect below.
    if (isOnOnboardingRoute) {
      return <Outlet />
    }
    return <BlockedOnboardingRedirect />
  }

  if (outcome === "start" || outcome === "resume") {
    if (!isOnOnboardingRoute) {
      const target = buildAuthenticatedNavigationTarget({
        decision,
        homeRedirectUrl: getHomeRedirectUrl(user),
      })

      return (
        <Navigate
          to={target.path}
          replace
          state={{ initialStepId: target.initialStepId }}
        />
      )
    }

    return <Outlet />
  }

  if (isOnOnboardingRoute) {
    return <Navigate to={SUPPLIER_DEFAULT_HOME_PATH} replace />
  }

  return <Outlet />
}

export const SupplierOnboardingGate = () => {
  const { t } = useTranslation("common")
  const { user, getHomeRedirectUrl } = useAuth()
  const location = useLocation()
  const gateQuery = useOnboardingEntryDecision(user)
  const state = user === null ? null : resolveGateState(gateQuery)

  if (!user || state === null) {
    return <Outlet />
  }

  switch (state.status) {
    case "loading":
      return <Loader fullPage />
    case "authError":
      return <ErrorBox title={t("dataLoad.error.title")} error={state.error} />
    case "retryable":
      return <OnboardingGateRetryScreen onRetry={state.retry} />
    case "ready":
      return (
        <ReadyOnboardingGate
          decision={state.decision}
          user={user}
          getHomeRedirectUrl={getHomeRedirectUrl}
          pathname={location.pathname}
          invitedOrganizationId={gateQuery.invitedOrganizationId}
        />
      )
  }
}
